import { Order, OrderStatus } from '../../orders-new/models/order.dto';
import { addDays, diffDays, dayOfMonth, formatDayMonth, formatMonthYear, isWeekend, todayIso, WEEKDAY_SHORT, dayOfWeek } from '../../../../utils/date-only';

export interface PlannerDayVm {
  offset: number;
  label: string;
  weekday: string;
  isWeekend: boolean;
  isToday: boolean;
}

export interface PlannerMonthVm {
  label: string;
  offset: number;
  span: number;
}

export type PlannerSchedule = 'onTrack' | 'atRisk' | 'behind' | 'done';

export interface PlannerBarVm {
  orderId: number;
  startOffset: number;
  span: number;
  rangeLabel: string;
  progressFraction: number;
  expectedFraction: number;
  schedule: PlannerSchedule;
  isPreview: boolean;
}

// A row holds a list of bars so per-item sub-rows can slot in later
// without changing the rendering model; today each order row has exactly one bar.
export interface PlannerRowVm {
  orderId: number;
  orderNumber: string;
  name: string;
  deadlineOffset: number | null;
  deadlineLabel: string | null;
  isDeadlineMissed: boolean;
  bars: PlannerBarVm[];
}

export interface TrayChipVm {
  orderId: number;
  orderNumber: string;
  name: string;
  deadlineLabel: string | null;
}

export type PlannerDrag =
  | { kind: 'move'; orderId: number; dayDelta: number }
  | { kind: 'resizeStart'; orderId: number; dayDelta: number }
  | { kind: 'resizeEnd'; orderId: number; dayDelta: number }
  | { kind: 'fromTray'; orderId: number; overOffset: number | null };

export const DEFAULT_PLAN_DAYS = 14;

export class PlannerViewModel {

  static isPlanned(order: Order): boolean {
    return order.plannedStartDate !== null && order.plannedEndDate !== null;
  }

  static computeRange(orders: Order[]): { rangeStart: string; dayCount: number } {
    const today = todayIso();
    let start = addDays(today, -7);
    let end = addDays(today, 35);

    for (const order of orders) {
      if (order.plannedStartDate && diffDays(order.plannedStartDate, start) > -3) {
        start = addDays(order.plannedStartDate, -3);
      }
      if (order.plannedEndDate && diffDays(end, order.plannedEndDate) > -7) {
        end = addDays(order.plannedEndDate, 7);
      }
      if (order.deadline && diffDays(end, order.deadline) > -7) {
        end = addDays(order.deadline, 7);
      }
    }

    // Snap to Monday so the axis always begins at a week boundary
    start = addDays(start, -((dayOfWeek(start) + 6) % 7));

    return { rangeStart: start, dayCount: diffDays(start, end) + 1 };
  }

  static mapDays(rangeStart: string, dayCount: number): PlannerDayVm[] {
    const today = todayIso();
    const days: PlannerDayVm[] = [];

    for (let offset = 0; offset < dayCount; offset++) {
      const iso = addDays(rangeStart, offset);
      days.push({
        offset,
        label: '' + dayOfMonth(iso),
        weekday: WEEKDAY_SHORT[dayOfWeek(iso)],
        isWeekend: isWeekend(iso),
        isToday: iso === today
      });
    }

    return days;
  }

  static mapMonths(rangeStart: string, dayCount: number): PlannerMonthVm[] {
    const months: PlannerMonthVm[] = [];

    for (let offset = 0; offset < dayCount; offset++) {
      const iso = addDays(rangeStart, offset);
      const label = formatMonthYear(iso);
      const current = months[months.length - 1];

      if (current && current.label === label) {
        current.span++;
      } else {
        months.push({ label, offset, span: 1 });
      }
    }

    return months;
  }

  static mapRows(orders: Order[], rangeStart: string, drag: PlannerDrag | null): PlannerRowVm[] {
    const planned = orders
      .filter(order => PlannerViewModel.isPlanned(order))
      .sort((a, b) =>
        diffDays(b.plannedStartDate!, a.plannedStartDate!) || a.orderNumber.localeCompare(b.orderNumber));

    const rows = planned.map(order => PlannerViewModel.mapRow(order, rangeStart, drag));

    const previewRow = PlannerViewModel.mapPreviewRow(orders, rangeStart, drag);

    if (previewRow) {
      rows.push(previewRow);
    }

    return rows;
  }

  static mapTray(orders: Order[]): TrayChipVm[] {
    return orders
      .filter(order => !PlannerViewModel.isPlanned(order) && order.status !== OrderStatus.done)
      .sort((a, b) => {
        if (a.deadline && b.deadline) return diffDays(b.deadline, a.deadline);
        if (a.deadline) return -1;
        if (b.deadline) return 1;
        return a.orderNumber.localeCompare(b.orderNumber);
      })
      .map(order => ({
        orderId: order.id,
        orderNumber: order.orderNumber,
        name: order.name,
        deadlineLabel: order.deadline ? `termin ${formatDayMonth(order.deadline)}` : null
      }));
  }

  private static mapRow(order: Order, rangeStart: string, drag: PlannerDrag | null): PlannerRowVm {
    let start = order.plannedStartDate!;
    let end = order.plannedEndDate!;

    if (drag && drag.kind !== 'fromTray' && drag.orderId === order.id) {
      if (drag.kind === 'move') {
        start = addDays(start, drag.dayDelta);
        end = addDays(end, drag.dayDelta);
      }
      if (drag.kind === 'resizeStart') {
        start = addDays(start, drag.dayDelta);
        if (diffDays(start, end) < 0) start = end;
      }
      if (drag.kind === 'resizeEnd') {
        end = addDays(end, drag.dayDelta);
        if (diffDays(start, end) < 0) end = start;
      }
    }

    const today = todayIso();
    const isDone = order.status === OrderStatus.done;
    const isDeadlineMissed = !isDone
      && order.deadline !== null
      && (diffDays(order.deadline, end) > 0 || diffDays(order.deadline, today) > 0);

    // Counted in parts, every tree line weighing the same whatever its unit; 1 exactly when Done (0112)
    const progressFraction = order.progressFraction;

    return {
      orderId: order.id,
      orderNumber: order.orderNumber,
      name: order.name,
      deadlineOffset: order.deadline ? diffDays(rangeStart, order.deadline) : null,
      deadlineLabel: order.deadline ? `termin ${formatDayMonth(order.deadline)}` : null,
      isDeadlineMissed,
      bars: [{
        orderId: order.id,
        startOffset: diffDays(rangeStart, start),
        span: diffDays(start, end) + 1,
        rangeLabel: `${formatDayMonth(start)} – ${formatDayMonth(end)}`,
        progressFraction,
        expectedFraction: isDone ? 0 : PlannerViewModel.computeExpected(start, end, today),
        schedule: PlannerViewModel.computeSchedule(isDone, isDeadlineMissed, start, end, today, progressFraction),
        isPreview: false
      }]
    };
  }

  // Linear expected progress across the planned window, in working days completed before today:
  // day one expects nothing, Saturdays and Sundays expect nothing (0113, no holiday calendar).
  // Feeds both the schedule color and the striped expected layer, so they can never disagree.
  private static computeExpected(start: string, end: string, today: string): number {
    const workdays = PlannerViewModel.countWorkdays(start, addDays(end, 1));

    if (workdays === 0) {
      return diffDays(end, today) > 0 ? 1 : 0;
    }

    const elapsedUntil = diffDays(today, end) < 0 ? addDays(end, 1) : today;
    return PlannerViewModel.countWorkdays(start, elapsedUntil) / workdays;
  }

  // Monday–Friday days in [from, until); 0 when until is not after from
  private static countWorkdays(from: string, until: string): number {
    let count = 0;

    for (let day = from; diffDays(day, until) > 0; day = addDays(day, 1)) {
      if (!isWeekend(day)) count++;
    }

    return count;
  }

  // The linear "estimated time" baseline may later be dropped for real time only —
  // every schedule rule lives in this one function so removing it stays a local change.
  private static computeSchedule(
    isDone: boolean,
    isDeadlineMissed: boolean,
    start: string,
    end: string,
    today: string,
    progressFraction: number
  ): PlannerSchedule {
    if (isDone) {
      return 'done';
    }

    // In working days, like expected itself
    const span = PlannerViewModel.countWorkdays(start, addDays(end, 1));
    const lagDays = (PlannerViewModel.computeExpected(start, end, today) - progressFraction) * span;
    const isWindowElapsed = diffDays(end, today) > 0;

    if (lagDays > 3 || isDeadlineMissed || isWindowElapsed) {
      return 'behind';
    }

    if (lagDays > 0) {
      return 'atRisk';
    }

    return 'onTrack';
  }

  private static mapPreviewRow(orders: Order[], rangeStart: string, drag: PlannerDrag | null): PlannerRowVm | null {
    if (!drag || drag.kind !== 'fromTray' || drag.overOffset === null) {
      return null;
    }

    const order = orders.find(x => x.id === drag.orderId);

    if (!order) {
      return null;
    }

    const start = addDays(rangeStart, drag.overOffset);
    const end = addDays(start, DEFAULT_PLAN_DAYS - 1);

    return {
      orderId: order.id,
      orderNumber: order.orderNumber,
      name: order.name,
      deadlineOffset: order.deadline ? diffDays(rangeStart, order.deadline) : null,
      deadlineLabel: order.deadline ? `termin ${formatDayMonth(order.deadline)}` : null,
      isDeadlineMissed: false,
      bars: [{
        orderId: order.id,
        startOffset: drag.overOffset,
        span: DEFAULT_PLAN_DAYS,
        rangeLabel: `${formatDayMonth(start)} – ${formatDayMonth(end)}`,
        progressFraction: 0,
        expectedFraction: 0,
        schedule: 'onTrack',
        isPreview: true
      }]
    };
  }
}
